<template>
    <div class="inbox">
        <!-- Toolbar -->
        <div class="inbox-toolbar">
            <h3 class="inbox-title">תיבת פניות ({{ displayedApplications.length }})</h3>
            <div class="toolbar-filters">
                <input
                    v-model="searchQuery"
                    type="text"
                    class="inbox-search"
                    placeholder="חיפוש לפי שם, טלפון או אימייל..."
                />
                <select v-model="filterStatus" class="inbox-filter" @change="onFilterChange">
                    <option value="">כל הסטטוסים</option>
                    <option value="new">חדש</option>
                    <option value="in_progress">בטיפול</option>
                    <option value="handled">טופל</option>
                </select>
                <select v-model="filterAdId" class="inbox-filter" @change="onFilterChange">
                    <option value="">כל המודעות</option>
                    <option v-for="ad in adOptions" :key="ad.id" :value="ad.id">{{ ad.title }}</option>
                </select>
            </div>
        </div>

        <!-- Date filter row -->
        <div class="date-filter-row">
            <label class="date-label">
                מתאריך:
                <input v-model="dateFrom" type="date" class="date-input" />
            </label>
            <label class="date-label">
                עד תאריך:
                <input v-model="dateTo" type="date" class="date-input" />
            </label>
            <button class="date-filter-btn" @click="applyDateFilter">
                <i class="material-icons" style="font-size:16px;vertical-align:middle;">filter_list</i>
                סנן לפי תאריך
            </button>
            <button
                v-if="dateFrom || dateTo"
                class="date-clear-btn"
                @click="clearDateFilter"
            >
                נקה תאריכים
            </button>
        </div>

        <!-- Loading (initial) -->
        <div v-if="isLoading" class="inbox-loading">
            <span class="spinner"></span>
            <span>טוען פניות...</span>
        </div>

        <!-- Empty -->
        <div v-else-if="!displayedApplications.length" class="inbox-empty">
            <i class="material-icons">inbox</i>
            <p>אין פניות עדיין</p>
        </div>

        <!-- Table -->
        <div v-else class="inbox-table-wrap" ref="tableWrap" @scroll="onScroll">
            <table class="inbox-table">
                <thead>
                    <tr>
                        <th>שם מלא</th>
                        <th>טלפון</th>
                        <th>אימייל</th>
                        <th>משרה</th>
                        <th>סניף</th>
                        <th>תאריך</th>
                        <th>סטטוס</th>
                        <th>פעולות</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="app in displayedApplications"
                        :key="app.id"
                        :class="{ unread: getStatus(app) === 'new' }"
                        @click="openDetail(app)"
                        class="inbox-row"
                    >
                        <td>{{ app.fullName || '—' }}</td>
                        <td>{{ app.phone || '—' }}</td>
                        <td>{{ app.email || '—' }}</td>
                        <td>{{ app.adTitle || '—' }}</td>
                        <td>{{ app.branch || '—' }}</td>
                        <td>{{ formatDateTime(app.submittedAt) }}</td>
                        <td>
                            <span class="status-badge" :class="'status-' + getStatus(app)">
                                {{ statusLabels[getStatus(app)] }}
                            </span>
                        </td>
                        <td @click.stop>
                            <button class="delete-btn" @click="handleDelete(app)" title="מחק">
                                <i class="material-icons">delete</i>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
            <!-- Load more indicator -->
            <div v-if="isLoadingMore" class="load-more-spinner">
                <span class="spinner"></span>
                <span>טוען עוד...</span>
            </div>
            <div v-else-if="!hasMore && applications.length > 0" class="load-more-end">
                אין עוד פניות
            </div>
        </div>

        <!-- Detail modal -->
        <div v-if="selectedApp" class="detail-overlay" @click.self="selectedApp = null">
            <div class="detail-modal">
                <div class="detail-header">
                    <div>
                        <h3>{{ selectedApp.adTitle }}</h3>
                        <p>{{ selectedApp.branch }} · {{ formatDateTime(selectedApp.submittedAt) }}</p>
                    </div>
                    <button class="detail-close-btn" @click="selectedApp = null">✕</button>
                </div>
                <div class="detail-body">
                    <div class="detail-row" v-if="selectedApp.fullName">
                        <span class="detail-label">שם מלא:</span>
                        <span>{{ selectedApp.fullName }}</span>
                    </div>
                    <div class="detail-row" v-if="selectedApp.phone">
                        <span class="detail-label">טלפון:</span>
                        <a :href="`tel:${selectedApp.phone}`">{{ selectedApp.phone }}</a>
                    </div>
                    <div class="detail-row" v-if="selectedApp.email">
                        <span class="detail-label">דוא"ל:</span>
                        <a :href="`mailto:${selectedApp.email}`">{{ selectedApp.email }}</a>
                    </div>
                    <div class="detail-row" v-if="selectedApp.idNumber">
                        <span class="detail-label">ת.ז.:</span>
                        <span>{{ selectedApp.idNumber }}</span>
                    </div>
                    <div class="detail-row" v-if="selectedApp.message">
                        <span class="detail-label">הודעה:</span>
                        <span class="detail-message">{{ selectedApp.message }}</span>
                    </div>
                    <div class="detail-row" v-if="selectedApp.cvUrl">
                        <span class="detail-label">קורות חיים:</span>
                        <a :href="selectedApp.cvUrl" target="_blank" class="cv-link">
                            <i class="material-icons">download</i>
                            הורד קורות חיים
                        </a>
                    </div>

                    <!-- Branch / role selections (only for applications that have them) -->
                    <div class="detail-row" v-if="selectedApp.selectedBranches && selectedApp.selectedBranches.length">
                        <span class="detail-label">סניפים מבוקשים:</span>
                        <span class="sel-chips">
                            <span v-for="b in selectedApp.selectedBranches" :key="b.id || b.label" class="sel-chip">{{ b.label || b.id }}</span>
                        </span>
                    </div>
                    <div class="detail-row" v-if="selectedApp.selectedRoles && selectedApp.selectedRoles.length">
                        <span class="detail-label">תפקידים מבוקשים:</span>
                        <span class="sel-chips">
                            <span v-for="r in selectedApp.selectedRoles" :key="r.id || r.label" class="sel-chip">{{ r.label || r.id }}</span>
                        </span>
                    </div>

                    <!-- Status control -->
                    <div class="detail-section">
                        <h4 class="detail-section-title">סטטוס פנייה</h4>
                        <select
                            :value="getStatus(selectedApp)"
                            @change="changeStatus(selectedApp, $event.target.value)"
                            class="status-select"
                            :class="'status-' + getStatus(selectedApp)"
                        >
                            <option value="new">חדש</option>
                            <option value="in_progress">בטיפול</option>
                            <option value="handled">טופל</option>
                        </select>
                    </div>

                    <!-- Treatment log -->
                    <div class="detail-section">
                        <h4 class="detail-section-title">תיעוד טיפול</h4>
                        <div class="log-input-row">
                            <input
                                v-model="newLogText"
                                type="text"
                                class="log-input"
                                placeholder="הוסף תיעוד טיפול..."
                                @keyup.enter="addTreatmentLog(selectedApp)"
                            />
                            <button
                                class="log-add-btn"
                                @click="addTreatmentLog(selectedApp)"
                                :disabled="!newLogText.trim()"
                            >
                                הוסף
                            </button>
                        </div>
                        <div class="log-list" v-if="getLogs(selectedApp).length">
                            <div class="log-entry" v-for="(log, i) in getLogs(selectedApp)" :key="i">
                                <div class="log-meta">
                                    <span class="log-author">{{ log.author }}</span>
                                    <span class="log-date">{{ formatDateTime(log.date) }}</span>
                                </div>
                                <div class="log-text">{{ log.text }}</div>
                            </div>
                        </div>
                        <div v-else class="log-empty">אין תיעודים עדיין</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { projectFirestore, firebase } from '@/firebase/config'
import { alert_confirm, slide_pop_successs, slide_pop_error } from '@/Methods/Msgs'
import store from '@/store'

const PAGE_SIZE = 20

export default {
    name: 'Inbox',
    setup() {
        const applications = ref([])
        const adOptions = ref([])
        const filterAdId = ref('')
        const filterStatus = ref('new')
        const searchQuery = ref('')
        const isLoading = ref(false)
        const isLoadingMore = ref(false)
        const hasMore = ref(true)
        const selectedApp = ref(null)
        const newLogText = ref('')
        const tableWrap = ref(null)
        const dateFrom = ref('')
        const dateTo = ref('')

        let lastDoc = null

        const statusLabels = {
            new: 'חדש',
            in_progress: 'בטיפול',
            handled: 'טופל'
        }

        const getStatus = (app) => app.status || 'new'

        const getLogs = (app) => {
            const logs = app.treatmentLogs || []
            return [...logs].sort((a, b) => {
                const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date)
                const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date)
                return dateB - dateA
            })
        }

        const displayedApplications = computed(() => {
            if (!searchQuery.value.trim()) return applications.value
            const q = searchQuery.value.trim().toLowerCase()
            return applications.value.filter(a =>
                (a.fullName || '').toLowerCase().includes(q) ||
                (a.phone || '').toLowerCase().includes(q) ||
                (a.email || '').toLowerCase().includes(q)
            )
        })

        const buildQuery = () => {
            let query = projectFirestore.collection('JobApplications')

            if (filterStatus.value) {
                query = query.where('status', '==', filterStatus.value)
            }
            if (filterAdId.value) {
                query = query.where('adId', '==', filterAdId.value)
            }

            if (dateFrom.value) {
                const start = new Date(dateFrom.value)
                start.setHours(0, 0, 0, 0)
                query = query.where('submittedAt', '>=', start)
            }
            if (dateTo.value) {
                const end = new Date(dateTo.value)
                end.setHours(23, 59, 59, 999)
                query = query.where('submittedAt', '<=', end)
            }

            query = query.orderBy('submittedAt', 'desc').limit(PAGE_SIZE)
            return query
        }

        const fetchPage = async (reset = false) => {
            if (reset) {
                applications.value = []
                lastDoc = null
                hasMore.value = true
                isLoading.value = true
            } else {
                if (!hasMore.value || isLoadingMore.value) return
                isLoadingMore.value = true
            }

            try {
                let query = buildQuery()
                if (lastDoc) {
                    query = query.startAfter(lastDoc)
                }

                const snap = await query.get()
                const newApps = snap.docs.map(doc => ({ id: doc.id, ref: doc.ref, ...doc.data() }))

                if (reset) {
                    applications.value = newApps
                } else {
                    applications.value = [...applications.value, ...newApps]
                }

                lastDoc = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : lastDoc
                hasMore.value = snap.docs.length >= PAGE_SIZE
            } catch (err) {
                console.error('Inbox fetchPage error:', err)
                slide_pop_error('שגיאה בטעינת הנתונים')
            } finally {
                isLoading.value = false
                isLoadingMore.value = false
            }
        }

        const backfillMissingStatus = async () => {
            try {
                const snap = await projectFirestore.collection('JobApplications').get()
                const batch = projectFirestore.batch()
                let count = 0
                snap.docs.forEach(doc => {
                    const data = doc.data()
                    if (!data.status) {
                        batch.update(doc.ref, { status: 'new' })
                        count++
                    }
                })
                if (count > 0) {
                    await batch.commit()
                    console.log(`Backfilled status on ${count} JobApplications`)
                }
            } catch (err) {
                console.error('backfillMissingStatus error:', err)
            }
        }

        const fetchAdOptions = async () => {
            try {
                const snap = await projectFirestore.collection('JobAds').get()
                adOptions.value = snap.docs.map(doc => ({ id: doc.id, title: doc.data().title }))
            } catch { /* silent */ }
        }

        const onFilterChange = () => {
            fetchPage(true)
        }

        const applyDateFilter = () => {
            if (dateTo.value && !dateFrom.value) {
                slide_pop_error('יש לבחור לפחות תאריך התחלה')
                return
            }
            fetchPage(true)
        }

        const clearDateFilter = () => {
            dateFrom.value = ''
            dateTo.value = ''
            fetchPage(true)
        }

        const onScroll = () => {
            const el = tableWrap.value
            if (!el || !hasMore.value || isLoadingMore.value) return
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
                fetchPage(false)
            }
        }

        const openDetail = async (app) => {
            selectedApp.value = app
            newLogText.value = ''
            if (!app.viewed) {
                try {
                    await app.ref.update({ viewed: true })
                    app.viewed = true
                } catch { /* silent */ }
            }
        }

        const changeStatus = async (app, newStatus) => {
            const oldStatus = getStatus(app)
            if (newStatus === oldStatus) return

            const userName = store.state.user?.name || 'לא ידוע'
            const logEntry = {
                text: `שינוי סטטוס: ${statusLabels[oldStatus]} → ${statusLabels[newStatus]}`,
                date: new Date(),
                author: userName
            }

            try {
                await app.ref.update({
                    status: newStatus,
                    treatmentLogs: firebase.firestore.FieldValue.arrayUnion(logEntry)
                })
                app.status = newStatus
                if (!app.treatmentLogs) app.treatmentLogs = []
                app.treatmentLogs.push(logEntry)
                slide_pop_successs('הסטטוס עודכן')
            } catch (err) {
                console.error('Inbox changeStatus error:', err)
                slide_pop_error('שגיאה בעדכון הסטטוס')
            }
        }

        const addTreatmentLog = async (app) => {
            const text = newLogText.value.trim()
            if (!text) return

            const userName = store.state.user?.name || 'לא ידוע'
            const logEntry = {
                text,
                date: new Date(),
                author: userName
            }

            try {
                await app.ref.update({
                    treatmentLogs: firebase.firestore.FieldValue.arrayUnion(logEntry)
                })
                if (!app.treatmentLogs) app.treatmentLogs = []
                app.treatmentLogs.push(logEntry)
                newLogText.value = ''
                slide_pop_successs('התיעוד נוסף')
            } catch (err) {
                console.error('Inbox addTreatmentLog error:', err)
                slide_pop_error('שגיאה בהוספת תיעוד')
            }
        }

        const handleDelete = async (app) => {
            const confirmed = await alert_confirm('האם למחוק פנייה זו לצמיתות?')
            if (!confirmed.isConfirmed) return
            try {
                await app.ref.delete()
                applications.value = applications.value.filter(a => a.id !== app.id)
                if (selectedApp.value && selectedApp.value.id === app.id) {
                    selectedApp.value = null
                }
                slide_pop_successs('הפנייה נמחקה')
            } catch (err) {
                console.error('Inbox handleDelete error:', err)
                slide_pop_error('שגיאה במחיקה')
            }
        }

        const formatDate = (ts) => {
            if (!ts) return '—'
            const date = ts.toDate ? ts.toDate() : new Date(ts)
            return date.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' })
        }

        const formatDateTime = (ts) => {
            if (!ts) return '—'
            const date = ts.toDate ? ts.toDate() : new Date(ts)
            return date.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' })
                + ' ' + date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })
        }

        onMounted(async () => {
            await backfillMissingStatus()
            fetchPage(true)
            fetchAdOptions()
        })

        return {
            applications, adOptions, filterAdId, filterStatus, searchQuery,
            dateFrom, dateTo,
            isLoading, isLoadingMore, hasMore, selectedApp, newLogText, tableWrap,
            statusLabels, displayedApplications, getStatus, getLogs,
            onFilterChange, onScroll, applyDateFilter, clearDateFilter,
            openDetail, changeStatus, addTreatmentLog,
            handleDelete, formatDate, formatDateTime
        }
    }
}
</script>

<style scoped>
.inbox {
    padding: 20px;
    height: 100%;
    overflow-y: auto;
    box-sizing: border-box;
    position: relative;
}

/* Toolbar */
.inbox-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    gap: 12px;
    flex-wrap: wrap;
}
.inbox-title {
    font-size: 18px;
    font-weight: 700;
    color: #222;
    margin: 0;
}
.toolbar-filters {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}
.inbox-search {
    padding: 9px 14px;
    border: 1.5px solid #ccc;
    border-radius: 20px;
    font-size: 14px;
    color: #333;
    background: #fff;
    outline: none;
    min-width: 220px;
    font-family: inherit;
    transition: border-color 0.2s;
}
.inbox-search:focus {
    border-color: #1579F2;
}
.inbox-filter {
    padding: 9px 14px;
    border: 1.5px solid #ccc;
    border-radius: 20px;
    font-size: 14px;
    color: #333;
    background: #fff;
    outline: none;
    min-width: 150px;
    font-family: inherit;
    transition: border-color 0.2s;
}
.inbox-filter:focus {
    border-color: #1579F2;
}

/* Date filter row */
.date-filter-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    flex-wrap: wrap;
}
.date-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #555;
}
.date-input {
    padding: 8px 12px;
    border: 1.5px solid #ccc;
    border-radius: 8px;
    font-size: 13px;
    font-family: inherit;
    outline: none;
    transition: border-color 0.2s;
}
.date-input:focus {
    border-color: #1579F2;
}
.date-filter-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 8px 16px;
    background: #1579F2;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    transition: background 0.2s;
    white-space: nowrap;
}
.date-filter-btn:hover {
    background: #0d5bbf;
}
.date-clear-btn {
    padding: 8px 14px;
    background: #f5f5f5;
    color: #666;
    border: 1.5px solid #ddd;
    border-radius: 8px;
    font-size: 13px;
    font-family: inherit;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s;
    white-space: nowrap;
}
.date-clear-btn:hover {
    background: #eee;
    border-color: #bbb;
    color: #333;
}

/* Loading / Empty */
.inbox-loading,
.inbox-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 20px;
    gap: 16px;
    color: #aaa;
}
.inbox-empty i {
    font-size: 64px;
}
.inbox-empty p {
    font-size: 15px;
    color: #888;
}
.inbox-loading {
    flex-direction: row;
    gap: 12px;
}

/* Table */
.inbox-table-wrap {
    overflow-x: auto;
    overflow-y: auto;
    max-height: calc(100vh - 200px);
    border-radius: 12px;
    border: 1px solid #e0e0e0;
}
.inbox-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    min-width: 700px;
}
.inbox-table thead tr {
    background: #1579F2;
    color: #fff;
    position: sticky;
    top: 0;
    z-index: 1;
}
.inbox-table th {
    padding: 12px 14px;
    text-align: right;
    font-weight: 600;
    white-space: nowrap;
}
.inbox-row {
    cursor: pointer;
    transition: background 0.15s;
}
.inbox-row:hover {
    background: #f0f6ff;
}
.inbox-row.unread {
    background: #fffde7;
    font-weight: 700;
}
.inbox-row.unread:hover {
    background: #fff9c4;
}
.inbox-table td {
    padding: 11px 14px;
    border-bottom: 1px solid #f0f0f0;
    color: #333;
    white-space: nowrap;
}

/* Status badge */
.status-badge {
    display: inline-block;
    border-radius: 50px;
    padding: 3px 10px;
    font-size: 11px;
    font-weight: 700;
}
.status-new {
    background: #FEF200;
    color: #000;
    border: 1px solid #ccc;
}
.status-in_progress {
    background: #FF9800;
    color: #fff;
}
.status-handled {
    background: #4CAF50;
    color: #fff;
}

/* Load more */
.load-more-spinner {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 16px;
    color: #888;
    font-size: 13px;
}
.load-more-end {
    text-align: center;
    padding: 14px;
    color: #aaa;
    font-size: 13px;
}

/* Delete button */
.delete-btn {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 1px solid #e0e0e0;
    background: #fafafa;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;
}
.delete-btn i {
    font-size: 17px;
    color: #999;
}
.delete-btn:hover {
    background: #fff0f0;
    border-color: #bb2429;
}
.delete-btn:hover i {
    color: #bb2429;
}

/* Detail modal */
.detail-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9000;
    padding: 16px;
}
.detail-modal {
    background: #fff;
    border-radius: 16px;
    width: 100%;
    max-width: 560px;
    max-height: 85vh;
    overflow-y: auto;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.25);
}
.detail-header {
    background: linear-gradient(135deg, #1579F2 0%, #0d5bbf 100%);
    padding: 18px 20px;
    border-radius: 16px 16px 0 0;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}
.detail-header h3 {
    color: #fff;
    font-size: 18px;
    font-weight: 700;
    margin: 0 0 4px;
}
.detail-header p {
    color: rgba(255,255,255,0.8);
    font-size: 13px;
    margin: 0;
}
.detail-close-btn {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: #fff;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    cursor: pointer;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background 0.2s;
}
.detail-close-btn:hover {
    background: rgba(255, 255, 255, 0.35);
}
.detail-body {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 14px;
}
.detail-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 14px;
}
.detail-label {
    font-weight: 700;
    color: #555;
    min-width: 80px;
    flex-shrink: 0;
}
.detail-message {
    color: #333;
    white-space: pre-wrap;
    line-height: 1.5;
}
.cv-link {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: #1579F2;
    font-weight: 600;
    text-decoration: none;
    padding: 6px 14px;
    border: 1.5px solid #1579F2;
    border-radius: 20px;
    font-size: 13px;
    transition: background 0.2s;
}
.cv-link:hover {
    background: #EBF4FF;
}
.cv-link i {
    font-size: 17px;
}
.sel-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.sel-chip {
    background: #EBF4FF;
    color: #0d5bbf;
    border: 1px solid #cfe0fa;
    border-radius: 20px;
    padding: 3px 10px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
}

/* Detail sections */
.detail-section {
    border-top: 1px solid #eee;
    padding-top: 16px;
    margin-top: 4px;
}
.detail-section-title {
    font-size: 15px;
    font-weight: 700;
    color: #333;
    margin: 0 0 10px;
}

/* Status select in modal */
.status-select {
    padding: 8px 14px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    font-family: inherit;
    border: 2px solid #ccc;
    outline: none;
    cursor: pointer;
    transition: border-color 0.2s;
}
.status-select.status-new {
    border-color: #e6d800;
    background: #fffde7;
    color: #000;
}
.status-select.status-in_progress {
    border-color: #FF9800;
    background: #fff3e0;
    color: #e65100;
}
.status-select.status-handled {
    border-color: #4CAF50;
    background: #e8f5e9;
    color: #2e7d32;
}

/* Treatment log */
.log-input-row {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
}
.log-input {
    flex: 1;
    padding: 9px 14px;
    border: 1.5px solid #ccc;
    border-radius: 8px;
    font-size: 13px;
    font-family: inherit;
    outline: none;
    transition: border-color 0.2s;
}
.log-input:focus {
    border-color: #1579F2;
}
.log-add-btn {
    padding: 9px 18px;
    background: #1579F2;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    transition: background 0.2s;
    white-space: nowrap;
}
.log-add-btn:hover:not(:disabled) {
    background: #0d5bbf;
}
.log-add-btn:disabled {
    background: #ccc;
    cursor: not-allowed;
}
.log-list {
    max-height: 200px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.log-entry {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 10px 12px;
    border-right: 3px solid #1579F2;
}
.log-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
    font-size: 11px;
    color: #888;
}
.log-author {
    font-weight: 700;
    color: #555;
}
.log-date {
    direction: ltr;
}
.log-text {
    font-size: 13px;
    color: #333;
    line-height: 1.4;
}
.log-empty {
    text-align: center;
    color: #aaa;
    font-size: 13px;
    padding: 12px;
}

/* Spinner */
.spinner {
    width: 22px;
    height: 22px;
    border: 3px solid #ddd;
    border-top-color: #1579F2;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
    display: inline-block;
}
@keyframes spin {
    to { transform: rotate(360deg); }
}
</style>
